import React, { useEffect, useMemo, useRef, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  FeatureBase, 
  FeatureSource 
} from "@models";
import { CHALLENGE_RATINGS, RARITY_NUM_TO_STR_MAP, RARITY_OPTIONS } from "@dndModels/Constants";
import { 
  CharacterFeatureSource,
  CharacterSheetInterface, 
  Creature, 
  CreatureGift, 
  BasicCreatureInstance, 
  Item, 
  MagicItemKeyword, 
  StandardFeatures, 
  SummonInstance,
  Summon
} from "@dndModels";

import { makeStyles } from "@utilities/theme_utilities";

import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import AddButton from "@components/input/buttons/AddButton";

interface Props {
  feature_bases: FeatureBase[];
  feature_source: FeatureSource | StandardFeatures | Creature | CreatureGift | Summon;
  cfs?: CharacterSheetInterface | BasicCreatureInstance | SummonInstance | CharacterFeatureSource;
  creature_instances?: BasicCreatureInstance[];
  disabled?: boolean;
  onExpand: (feature_base: FeatureBase) => void;
  onAdd: () => void;
  onReorder: (updated: FeatureBase[]) => void;
  level_filter?: number;
  onLevelFilterChange?: (changed: number) => void;
  simple?: boolean;
  height: number;
}

export default function FeatureBasesInput({
  feature_bases,
  feature_source,
  cfs,
  creature_instances,
  onExpand,
  onAdd,
  onReorder,
  disabled = false,
  level_filter = -1,
  onLevelFilterChange,
  simple = false,
  height
}: Props) {
  const [use_level_filter, setUseLevelFilter] = useState(level_filter);

  // Local working copy so we can update position_at_level without a parent callback
  const [local_bases, setLocalBases] = useState<FeatureBase[]>(() =>
    feature_bases.map(fb => fb.clone())
  );

  // Keep local_bases in sync when the prop changes from outside
  useEffect(() => {
    setLocalBases(feature_bases.map(fb => fb.clone()));
  }, [feature_bases]);

  const dragId = useRef<string | null>(null);

  const rarity_options = useMemo(() => {
    if (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) {
      return RARITY_OPTIONS.filter(o => +o.id !== -1 && +o.id >= feature_source.min_rarity && +o.id <= feature_source.max_rarity);
    }
    return [];
  }, [feature_source]);
  
  useEffect(() => {
    if (level_filter !== use_level_filter) {
      setUseLevelFilter(level_filter);
    }
  }, [level_filter, use_level_filter, setUseLevelFilter]);

  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        '&.tableHeader': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.secondary.dark,
        },
        '&.alternate': {
          color: theme.palette.primary.contrastText,
          backgroundColor: theme.palette.primary.dark,
        },
      },
    });
  });

  // const descriptionStyle = (): React.CSSProperties => {
  //   const descWidth = Math.floor(window.innerWidth * 0.8);
  //   return {
  //     width: `${descWidth}px`,
  //     whiteSpace: "nowrap",
  //     overflow: "hidden",
  //     textOverflow: "ellipsis",
  //   };
  // };

  const renderLevelSelect = () => {
    if (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) {
      return (
        <SelectStringBox  
          name="Filter for Rarity"
          options={rarity_options}
          extra_options={["All"]}
          value={level_filter === -1 ? "All" : `${level_filter}`}
          onChange={(changed: string) => {
            if (onLevelFilterChange) {
              onLevelFilterChange(changed === "All" ? -1 : +changed);
            }
          }}
        />
      );
    } else {
      return (
        <SelectStringBox
          name="Filter for Level"
          options={CHALLENGE_RATINGS}
          extra_options={["All"]}
          value={level_filter === -1 ? "All" : `${level_filter}`}
          onChange={(changed: string) => {
            if (onLevelFilterChange) {
              onLevelFilterChange(changed === "All" ? -1 : +changed);
            }
          }}
        />
      );
    }
  };

  const sorted_bases = useMemo(() => {
    return local_bases
      .filter(o => (!disabled || o.display) && (level_filter === -1 || o.level === level_filter))
      .sort((a, b) => {
        if (a.level !== b.level) return +a.level < +b.level ? -1 : 1;
        if (a.position_at_level !== b.position_at_level) return a.position_at_level - b.position_at_level;
        return a.fb_name.localeCompare(b.fb_name);
      });
  }, [local_bases, disabled, level_filter]);

  const handleDragStart = (id: string) => {
    dragId.current = id;
  };
  
  const handleDrop = (targetId: string) => {
    const sourceId = dragId.current;
    dragId.current = null;
    if (!sourceId || sourceId === targetId) return;

    const source = local_bases.find(o => o.true_id === sourceId);
    const target = local_bases.find(o => o.true_id === targetId);
    if (!source || !target || source.level !== target.level) return;

    const this_level = sorted_bases.filter(o => o.level === source.level);
    const from_idx = this_level.findIndex(o => o.true_id === sourceId);
    const to_idx = this_level.findIndex(o => o.true_id === targetId);

    const reordered = [...this_level];
    reordered.splice(from_idx, 1);
    reordered.splice(to_idx, 0, source);

    const new_positions = new Map(reordered.map((fb, i) => [fb.true_id, i]));

    const updated = local_bases.map(fb => {
      if (fb.level !== source.level) return fb;
      const new_pos = new_positions.get(fb.true_id);
      if (new_pos === undefined || fb.position_at_level === new_pos) return fb;
      const cloned = fb.clone();
      cloned.position_at_level = new_pos;
      return cloned;
    });

    setLocalBases(updated);
    onReorder(updated);
  };

  const { classes, cx } = useStyles();

  return (
    <Grid container spacing={0} direction="row">
      <Grid size={3}>
        <span className="header padded">
          Feature Bases
        </span>
        { !disabled &&
          <AddButton onClick={() => { onAdd(); }} />
        }
      </Grid>
      <Grid size={9}>
        { level_filter !== undefined && renderLevelSelect() }
      </Grid>
      <Grid size={12} className={cx(classes.root, "tableHeader")}
        container spacing={0} direction="row">
        { !disabled && <Grid size="auto" sx={{ width: 32 }} /> }
        <Grid size={2}>Name</Grid>
        <Grid size={1}>
          { (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) ? "Rarity" : "Level" }
        </Grid>
        <Grid size={9}>Description</Grid>
      </Grid>
      <Grid size={12}
        style={{ 
          height: height === 0 ? "inherit" : height - 40,
          overflowY: "auto", 
          width: "100%", 
          overflowX: "hidden",
        }}>
        <Grid container spacing={0} direction="row">
          { sorted_bases.map((feature_base, key) => {
            // const use_cfs = creature_instances
            //   ? creature_instances.find(o => o.challenge_rating === feature_base.level)
            //   : cfs;
            // const description = simple && feature_base.features.length === 1
            //   ? feature_base.features[0].description
            //   : feature_base.description;

            return (
              <Grid size={12} key={feature_base.true_id}
                container spacing={0} direction="row"
                draggable={!disabled}
                onDragStart={!disabled ? () => handleDragStart(feature_base.true_id) : undefined}
                onDragOver={!disabled ? (e) => e.preventDefault() : undefined}
                onDrop={!disabled ? (e) => { e.preventDefault(); handleDrop(feature_base.true_id); } : undefined}
                onClick={() => { onExpand(feature_base); }}
                className={cx(classes.root, (key % 2 === 1 ? "alternate clickable" : "clickable"))}>
                { !disabled && (
                  <Grid size="auto" sx={{ width: 32, display: 'flex', alignItems: 'center', cursor: 'grab', color: 'text.secondary' }}
                    onClick={(e) => e.stopPropagation()}>
                    ⠿
                  </Grid>
                )}
                <Grid size={2}>
                  { feature_base.fb_name === "" ? "Nameless Feature Base" : feature_base.fb_name }
                </Grid>
                <Grid size={1}>
                  { (feature_source instanceof Item || feature_source instanceof MagicItemKeyword) ?
                    RARITY_NUM_TO_STR_MAP[feature_base.level]
                  : feature_base.level }
                </Grid>
                <Grid size={9}>
                  {/* <div style={descriptionStyle()}>
                    <DisplayStringWithReplacements display_string={description} cfs={use_cfs} />
                  </div> */}
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
}